// src/pages/NotesConjoncture.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  CardMedia,
  Box,
} from '@mui/material';
import Footer from '../navbar/Footer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { getPublications } from '../services/publicationService';

function NotesConjoncture() {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [notesData, setNotesData] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'nc', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le tri et le filtre
            publicationDate: item.publication_date ? new Date(item.publication_date) : null,
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setNotesData(sorted);
          setFilteredNotes(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);

    let filtered;
    if (year === 'Tous') {
      filtered = [...notesData];
    } else {
      filtered = notesData.filter((note) => {
        const noteYear = note.date.getFullYear();
        return noteYear === parseInt(year);
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredNotes(filtered);
    setVisibleCount(4); // réinitialiser l'affichage
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      secondary: { main: '#f50057' },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 'bold',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    },
  });

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = notesData.map((note) => note.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [notesData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '50px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
              Notes de Conjoncture
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '50px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
              Notes de Conjoncture
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '50px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
            Notes de Conjoncture
          </Typography>
          <Divider style={{ margin: '20px 0' }} />
          <Typography variant="body1" align="justify" gutterBottom style={{ marginBottom: '20px' }}>
            Cette section présente les Notes de Conjoncture de l'INSBU, qui évaluent périodiquement l'économie burundaise en abordant des thèmes comme la croissance, l'inflation et l'emploi.
          </Typography>

          <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Sélectionnez par l'année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionnez par l'année de publication"
              variant="outlined"
            >
              <MenuItem value="Tous">Tous les notes</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredNotes.slice(0, visibleCount).map((note, index) => (
              <Grid item xs={12} sm={6} md={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'row', height: '100%' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 51, height: 80, m: 2, cursor: 'pointer' }}
                    image={note.image}
                    alt={note.title}
                    onClick={() => window.open(note.link, '_blank')}
                  />
                  <CardContent style={{ flex: 1, padding: '20px' }}>
                    <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                      <a
                        href={note.link}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadIcon /> {note.title}
                      </a>
                    </Typography>
                    <Typography variant="body2" component="div" gutterBottom style={{ color: '#555', marginBottom: '20px' }}>
                      {note.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredNotes.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Notes
            </Button>
          )}
          <br />
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            <strong>Note :</strong> Les Notes de Conjoncture sont publiées trimestriellement, généralement <strong>45 jours</strong> après la clôture de chaque trimestre. Les dates prévues pour la publication sont les suivantes :
            <ul style={{ listStyleType: 'circle', margin: '10px 0 10px 20px', padding: 0 }}>
              <li><strong>15 février</strong> - Publication des données pour le quatrième trimestre</li>
              <li><strong>15 mai</strong> - Publication des données pour le premier trimestre</li>
              <li><strong>15 août</strong> - Publication des données pour le deuxième trimestre</li>
              <li><strong>15 novembre</strong> - Publication des données pour le troisième trimestre</li>
            </ul>
          </Typography>

          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
              onClick={() => navigate('/bulletin')}
              style={{
                marginTop: '5px',
                backgroundColor: '#388E3C',
                color: 'white',
              }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page des bulletins
            </Button>
          </Box>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default NotesConjoncture;